import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/useAuth.js';

import { InsightBar, statusColor } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { showToast } from '../../components/ui/Toaster.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { PurchaseOrderCreateModal } from './PurchaseOrderCreateModal.jsx';
import { ReceivePurchaseOrderModal } from './ReceivePurchaseOrderModal.jsx';

export function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const palette = useChartPalette();
  // UX mirror only - the inventory-service enforces the admin requirement.
  const { roles } = useAuth();
  const canDecideApprovals = roles.includes('admin');
  const [createOpen, setCreateOpen] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const { data: antwort, isLoading, error } = useQuery({
    queryKey: ['inventory', 'purchase-orders'],
    queryFn: () => inventoryApi.listPurchaseOrders(),
  });
  const data = antwort?.items || [];
  const meta = antwort?.meta;

  const { data: insights } = useQuery({
    queryKey: ['inventory', 'po-insights'],
    queryFn: () => inventoryApi.poInsights().then((r) => r.items),
  });
  const byStatus = insights?.byStatus || [];
  const invalidatePOs = () => {
    queryClient.invalidateQueries({ queryKey: ['inventory', 'purchase-orders'] });
    queryClient.invalidateQueries({ queryKey: ['inventory', 'po-insights'] });
  };
  // 403s already surface via the global forbidden handler - avoid a double toast.
  const toastError = (err) => {
    if (err?.status !== 403) showToast(err.message);
  };

  const confirm = useMutation({
    mutationFn: (id) => inventoryApi.confirmPO(id),
    onSuccess: (po) => {
      invalidatePOs();
      // Above the approval threshold confirm parks the PO instead of confirming.
      if (po?.status === 'PENDING_APPROVAL') {
        showToast(t('inv.po.approval.submitted'), 'success');
      }
    },
    onError: toastError,
  });
  const cancel = useMutation({
    mutationFn: (id) => inventoryApi.cancelPO(id),
    onSuccess: invalidatePOs,
    onError: toastError,
  });
  const approve = useMutation({
    mutationFn: (id) => inventoryApi.approvePO(id),
    onSuccess: invalidatePOs,
    onError: toastError,
  });
  const reject = useMutation({
    mutationFn: (id) => inventoryApi.rejectPO(id),
    onSuccess: () => {
      invalidatePOs();
      setRejectTarget(null);
    },
    onError: (err) => {
      setRejectTarget(null);
      toastError(err);
    },
  });

  const columns = [
    {
      key: 'poNumber',
      label: t('col.number'),
      render: (r) => <Link to={`/inventory/purchase-orders/${r.id}`}>{r.poNumber}</Link>,
    },
    { key: 'orderDate',    label: t('inv.po.col.date'),     render: (r) => formatDate(r.orderDate) },
    { key: 'expectedDate', label: t('inv.po.col.expected'), render: (r) => formatDate(r.expectedDate) },
    { key: 'totalAmount',  label: t('col.total'), align: 'right', render: (r) => formatCurrency(r.totalAmount) },
    { key: 'status',       label: t('col.status'), render: (r) => <Badge status={r.status} /> },
    {
      key: 'actions',
      label: '',
      render: (r) => {
        if (r.status === 'DRAFT') {
          return (
            <div style={{ display: 'flex', gap: 6 }}>
              <Button
                size="sm"
                variant="primary"
                onClick={() => confirm.mutate(r.id)}
                disabled={confirm.isPending}
              >
                {t('inv.po.action.confirm')}
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => cancel.mutate(r.id)}
                disabled={cancel.isPending}
              >
                {t('inv.po.action.cancel')}
              </Button>
            </div>
          );
        }
        if (r.status === 'PENDING_APPROVAL') {
          if (!canDecideApprovals) {
            return null;
          }
          return (
            <div style={{ display: 'flex', gap: 6 }}>
              <Button
                size="sm"
                variant="primary"
                onClick={() => approve.mutate(r.id)}
                disabled={approve.isPending}
              >
                {t('inv.po.action.approve')}
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => setRejectTarget(r)}
                disabled={reject.isPending}
              >
                {t('inv.po.action.reject')}
              </Button>
            </div>
          );
        }
        if (r.status === 'CONFIRMED' || r.status === 'PARTIAL') {
          return (
            <Button size="sm" variant="primary" onClick={() => setReceiveTarget(r)}>
              {t('inv.po.action.receive')}
            </Button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('inv.po.title')}</h1>
          <p className="muted">{t('inv.po.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('inv.po.action.new')}
          </Button>
        </div>
      </header>

      {byStatus.length > 0 && (
        <InsightBar
          title={t('inv.po.insight.dist')}
          labels={byStatus.map((b) => t(`status.${b.status}`))}
          values={byStatus.map((b) => Number(b.amount))}
          colors={byStatus.map((b) => statusColor(palette, b.status))}
          formatValue={(n) => formatCurrency(n)}
          metrics={[
            { label: t('inv.po.insight.open'), value: insights.open.count },
            { label: t('inv.po.insight.openValue'), value: formatCurrency(insights.open.amount) },
            { label: t('inv.po.insight.awaiting'), value: formatCurrency(insights.awaitingReceipt) },
            { label: t('inv.po.insight.received'), value: formatCurrency(insights.received) },
          ]}
        />
      )}

      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} error={error} meta={meta} emptyMessage={t('empty.po')} onRowClick={(r) => navigate(`/inventory/purchase-orders/${r.id}`)} />
      <PurchaseOrderCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ReceivePurchaseOrderModal po={receiveTarget} onClose={() => setReceiveTarget(null)} />
      <ConfirmDialog
        open={Boolean(rejectTarget)}
        title={t('inv.po.action.reject')}
        message={t('inv.po.approval.confirmReject')}
        confirmLabel={t('inv.po.action.reject')}
        variant="danger"
        isPending={reject.isPending}
        onConfirm={() => reject.mutate(rejectTarget.id)}
        onClose={() => setRejectTarget(null)}
      />
    </section>
  );
}
